import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Webhook,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Search,
  ArrowRight,
  MinusCircle,
  AlertTriangle,
  Flag,
  CheckCheck,
} from 'lucide-react';

import { DataTable, type Column } from '@/components/common/DataTable';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { ErrorState } from '@/components/common/ErrorState';
import { KpiCard } from '@/components/common/KpiCard';
import { RequestResponsePanel } from '@/components/common/RequestResponsePanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { observabilityService } from '@/services/observability';
import type {
  InboundWebhookEvent,
  InboundWebhookProcessingOutcome,
  InboundWebhookStatus,
  ListInboundWebhooksQuery,
  PlatformSupportTicketSeverity,
} from '@/types/observability';
import { fmt } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';

type StatusFilter = 'all' | InboundWebhookStatus;
type OutcomeFilter = 'all' | InboundWebhookProcessingOutcome;

export function InboundWebhooksPage({
  embedded = false,
}: { embedded?: boolean } = {}) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [awbSearch, setAwbSearch] = useState('');
  const [selected, setSelected] = useState<InboundWebhookEvent | null>(null);
  const debouncedAwb = useDebounced(awbSearch);

  const query: ListInboundWebhooksQuery = useMemo(
    () => ({
      status: statusFilter === 'all' ? undefined : statusFilter,
      processingOutcome: outcomeFilter === 'all' ? undefined : outcomeFilter,
      provider: providerFilter === 'all' ? undefined : providerFilter,
      awbNumber: debouncedAwb || undefined,
      pageSize: 200,
    }),
    [statusFilter, outcomeFilter, providerFilter, debouncedAwb],
  );

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['observability', 'inbound-webhooks', query],
    queryFn: () => observabilityService.listInboundWebhooks(query),
    refetchInterval: 15_000, // Poll every 15s while page is open
  });

  const rows = data?.rows ?? [];

  const summary = useMemo(() => {
    let accepted = 0;
    let rejected = 0;
    let applied = 0;
    let skipped = 0;
    let errored = 0;
    const providers = new Set<string>();
    let last: string | null = null;
    for (const r of rows) {
      providers.add(r.provider);
      if (r.status === 'ACCEPTED') accepted += 1;
      else rejected += 1;
      if (r.processingOutcome === 'APPLIED') applied += 1;
      else if (r.processingOutcome === 'SKIPPED') skipped += 1;
      else if (r.processingOutcome === 'ERRORED') errored += 1;
      if (!last || r.receivedAt > last) last = r.receivedAt;
    }
    return {
      total: rows.length,
      accepted,
      rejected,
      applied,
      skipped,
      errored,
      providerCount: providers.size,
      last,
    };
  }, [rows]);

  const providerOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) set.add(r.provider);
    return Array.from(set).sort();
  }, [rows]);

  const columns: Column<InboundWebhookEvent>[] = [
    {
      key: 'receivedAt',
      header: 'Received at',
      width: '200px',
      cell: (r) => (
        <span className="font-mono text-[11.5px] tabular-nums">
          {fmt.dateTime(r.receivedAt)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Received',
      width: '120px',
      cell: (r) =>
        r.status === 'ACCEPTED' ? (
          <Badge variant="success" className="gap-1 text-[10px]">
            <CheckCircle2 className="h-3 w-3" /> Accepted
          </Badge>
        ) : (
          <Badge variant="destructive" className="gap-1 text-[10px]">
            <XCircle className="h-3 w-3" /> Rejected
          </Badge>
        ),
    },
    {
      key: 'processingOutcome',
      header: 'Outcome',
      width: '120px',
      cell: (r) => <OutcomeBadge outcome={r.processingOutcome} />,
    },
    {
      key: 'provider',
      header: 'Partner',
      width: '180px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="text-[12.5px] font-medium text-foreground">
            {r.partnerName ?? r.sourceCode}
          </div>
          <div className="text-[10.5px] text-muted-foreground">
            {r.provider}
            {r.brandName ? ` · ${r.brandName}` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'awb',
      header: 'AWB / Tracking',
      width: '160px',
      cell: (r) =>
        r.awbNumber ? (
          <span className="font-mono text-[11.5px]">{r.awbNumber}</span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'rawStatus',
      header: 'Reported status',
      width: '180px',
      cell: (r) =>
        r.rawStatus ? (
          <div className="space-y-0.5">
            <div className="text-[12px] text-foreground">{r.rawStatus}</div>
            {r.normalizedStatus ? (
              <div className="text-[10.5px] text-muted-foreground">
                → <span className="font-mono">{r.normalizedStatus}</span>
              </div>
            ) : null}
          </div>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'note',
      header: 'Note',
      cell: (r) =>
        r.errorReason ? (
          <span
            className={
              r.status === 'REJECTED'
                ? 'text-[11.5px] text-destructive'
                : 'text-[11.5px] text-muted-foreground'
            }
          >
            {r.errorReason}
          </span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'sourceIp',
      header: 'Source IP',
      width: '130px',
      cell: (r) =>
        r.sourceIp ? (
          <span className="font-mono text-[10.5px] text-muted-foreground">{r.sourceIp}</span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">—</span>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      {embedded ? (
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      ) : (
        <PageHeader
          eyebrow="Developer"
          title="Inbound webhooks"
          description="Every webhook delivery partners (Shiprocket, DHL, BlueDart, …) pushed to this tenant in the last 30 days. Both accepted and rejected events land here so you can troubleshoot integrations end-to-end."
          actions={
            <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          }
        />
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Events (window)"
          value={fmt.number(summary.total)}
          tone="primary"
          icon={Webhook}
        />
        <KpiCard
          label="Applied"
          hint="Caused a state change"
          value={fmt.number(summary.applied)}
          tone="success"
          icon={ArrowRight}
        />
        <KpiCard
          label="Skipped"
          hint="Stored but no state change (unknown AWB, status unchanged, …)"
          value={fmt.number(summary.skipped)}
          tone="default"
          icon={MinusCircle}
        />
        <KpiCard
          label="Errored"
          hint="Accepted but downstream apply threw — needs ops attention"
          value={fmt.number(summary.errored)}
          tone={summary.errored ? 'destructive' : 'default'}
          icon={AlertTriangle}
        />
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:max-w-2xl">
        <KpiCard
          label="Accepted (signature OK)"
          value={fmt.number(summary.accepted)}
          tone="default"
        />
        <KpiCard
          label="Rejected (signature/parse)"
          value={fmt.number(summary.rejected)}
          tone={summary.rejected ? 'destructive' : 'default'}
        />
        <KpiCard
          label="Last received"
          value={summary.last ? fmt.relative(summary.last) : '—'}
          tone="default"
        />
      </div>

      <SectionCard
        title="Recent events"
        description="Auto-refreshing every 15s. Click any row to see full event detail."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
              <SelectTrigger className="h-8 w-[140px] text-[12px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="ACCEPTED">Accepted</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={outcomeFilter} onValueChange={(v) => setOutcomeFilter(v as OutcomeFilter)}>
              <SelectTrigger className="h-8 w-[140px] text-[12px]">
                <SelectValue placeholder="Outcome" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All outcomes</SelectItem>
                <SelectItem value="APPLIED">Applied</SelectItem>
                <SelectItem value="SKIPPED">Skipped</SelectItem>
                <SelectItem value="ERRORED">Errored</SelectItem>
              </SelectContent>
            </Select>
            <Select value={providerFilter} onValueChange={setProviderFilter}>
              <SelectTrigger className="h-8 w-[160px] text-[12px]">
                <SelectValue placeholder="Provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All providers</SelectItem>
                {providerOptions.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative w-44">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={awbSearch}
                onChange={(e) => setAwbSearch(e.target.value)}
                placeholder="AWB / tracking #"
                className="h-8 pl-8 text-[13px]"
              />
            </div>
          </div>
        }
      >
        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            onRowClick={setSelected}
            density="compact"
            emptyTitle="No inbound webhooks yet"
            emptyDescription="Once your delivery partners (Shiprocket, DHL, …) push a tracking update, it'll appear here."
          />
        )}
      </SectionCard>

      <InboundWebhookDetailSheet
        event={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </div>
  );
}

interface DetailProps {
  event: InboundWebhookEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function InboundWebhookDetailSheet({ event, open, onOpenChange }: DetailProps) {
  const [reportOpen, setReportOpen] = useState(false);
  if (!event) return null;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-md overflow-y-auto sm:max-w-lg">
        <SheetHeader className="flex flex-row items-center justify-between space-y-0 pr-6">
          <SheetTitle>Inbound webhook event</SheetTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setReportOpen(true)}
            className="gap-1"
          >
            <Flag className="h-3.5 w-3.5" />
            Report issue
          </Button>
        </SheetHeader>
        <div className="space-y-4 pt-4 text-[13px]">
          <Field label="Received">
            <span className="font-mono">{fmt.dateTime(event.receivedAt)}</span>
          </Field>
          <Field label="Status">
            {event.status === 'ACCEPTED' ? (
              <Badge variant="success" className="gap-1">
                <CheckCircle2 className="h-3 w-3" /> Accepted
              </Badge>
            ) : (
              <Badge variant="destructive" className="gap-1">
                <XCircle className="h-3 w-3" /> Rejected
              </Badge>
            )}
          </Field>
          <Field label="Outcome">
            <div className="space-y-1">
              <OutcomeBadge outcome={event.processingOutcome} />
              {event.processingNote && (
                <div className="text-[11.5px] text-muted-foreground">
                  {event.processingNote}
                </div>
              )}
            </div>
          </Field>
          <Field label="Source">
            <div className="space-y-0.5">
              <div className="font-medium">{event.partnerName ?? event.sourceCode}</div>
              <div className="text-[11.5px] text-muted-foreground">
                {event.provider}
                {event.brandName ? ` · ${event.brandName}` : ''}
                {' · '}
                <span className="font-mono">{event.sourceCode}</span>
              </div>
            </div>
          </Field>
          <Field label="AWB / Tracking">
            {event.awbNumber ? (
              <span className="font-mono">{event.awbNumber}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </Field>
          <Field label="Reported status">
            {event.rawStatus ? (
              <div className="space-y-0.5">
                <div>{event.rawStatus}</div>
                {event.normalizedStatus ? (
                  <div className="text-[11.5px] text-muted-foreground">
                    Mapped to <span className="font-mono">{event.normalizedStatus}</span>
                  </div>
                ) : null}
              </div>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </Field>
          {event.location ? <Field label="Location">{event.location}</Field> : null}
          {event.errorReason ? (
            <Field label="Note">
              <span
                className={
                  event.status === 'REJECTED' ? 'text-destructive' : 'text-muted-foreground'
                }
              >
                {event.errorReason}
              </span>
            </Field>
          ) : null}
          <Field label="Source IP">
            {event.sourceIp ? (
              <span className="font-mono">{event.sourceIp}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </Field>
          <Field label="Payload size">
            {event.contentLength != null
              ? `${event.contentLength.toLocaleString()} bytes`
              : '—'}
          </Field>
          {event.payloadHash ? (
            <Field label="Payload SHA-256">
              <span className="font-mono text-[11px] break-all text-muted-foreground">
                {event.payloadHash}
              </span>
            </Field>
          ) : null}
          <Field label="Event ID">
            <span className="font-mono text-[11px] text-muted-foreground">{event.id}</span>
          </Field>

          <div className="pt-2">
            <RequestResponsePanel
              request={{
                method: 'POST',
                headers: event.requestHeaders,
                body: event.rawPayload,
              }}
              emptyHint="No raw body captured for this event. Older events from before observability v2 only have the hash; new events save the sanitized body."
            />
          </div>
        </div>
      </SheetContent>
      <ReportIssueDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        event={event}
      />
    </Sheet>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function OutcomeBadge({
  outcome,
}: {
  outcome: InboundWebhookProcessingOutcome | null;
}) {
  if (!outcome) {
    return <span className="text-[11.5px] text-muted-foreground">—</span>;
  }
  if (outcome === 'APPLIED') {
    return (
      <Badge variant="success" className="gap-1 text-[10px]">
        <ArrowRight className="h-3 w-3" /> Applied
      </Badge>
    );
  }
  if (outcome === 'SKIPPED') {
    return (
      <Badge variant="muted" className="gap-1 text-[10px]">
        <MinusCircle className="h-3 w-3" /> Skipped
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1 text-[10px]">
      <AlertTriangle className="h-3 w-3" /> Errored
    </Badge>
  );
}

// ── Report-issue dialog ───────────────────────────────────────────

const SEVERITY_OPTIONS: {
  value: PlatformSupportTicketSeverity;
  label: string;
  hint: string;
}[] = [
  { value: 'LOW', label: 'Low', hint: 'A nice-to-have or a question — no operational impact.' },
  { value: 'MEDIUM', label: 'Medium', hint: 'Workflow has a workaround but it slows ops.' },
  { value: 'HIGH', label: 'High', hint: 'Significant slowdown or partial outage — no easy workaround.' },
  { value: 'CRITICAL', label: 'Critical', hint: 'Production is down or revenue is being lost right now.' },
];

function defaultSubject(event: InboundWebhookEvent): string {
  const provider = event.provider;
  const rawStatus = event.rawStatus ?? event.status;
  const awb = event.awbNumber ? ` (AWB ${event.awbNumber})` : '';
  return `${provider} webhook: ${rawStatus}${awb}`;
}

function defaultBody(event: InboundWebhookEvent): string {
  const lines: string[] = [
    'What I expected vs what I saw:',
    '',
    '— Replace this with a short description —',
    '',
    '— Diagnostic context (auto-filled, please leave intact) —',
    `Event ID: ${event.id}`,
    `Received: ${event.receivedAt}`,
    `Provider: ${event.provider}`,
    `Partner code: ${event.sourceCode}`,
    `Status: ${event.status}`,
    event.processingOutcome
      ? `Outcome: ${event.processingOutcome}${event.processingNote ? ` — ${event.processingNote}` : ''}`
      : 'Outcome: —',
    event.awbNumber ? `AWB: ${event.awbNumber}` : '',
    event.rawStatus ? `Reported status: ${event.rawStatus}` : '',
    event.normalizedStatus ? `Mapped to: ${event.normalizedStatus}` : '',
    event.errorReason ? `Error reason: ${event.errorReason}` : '',
  ].filter(Boolean);
  return lines.join('\n');
}

function ReportIssueDialog({
  open,
  onOpenChange,
  event,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  event: InboundWebhookEvent;
}) {
  const [subject, setSubject] = useState(() => defaultSubject(event));
  const [severity, setSeverity] = useState<PlatformSupportTicketSeverity>('MEDIUM');
  const [body, setBody] = useState(() => defaultBody(event));
  const [submitted, setSubmitted] = useState<string | null>(null);

  // Re-seed when the dialog re-opens against a different event.
  // (Cheaper than threading a key from the parent.)
  if (!open && submitted) {
    // Reset after close.
    queueMicrotask(() => {
      setSubmitted(null);
      setSubject(defaultSubject(event));
      setBody(defaultBody(event));
      setSeverity('MEDIUM');
    });
  }

  const mutation = useMutation({
    mutationFn: () =>
      observabilityService.createSupportTicket({
        subject: subject.trim(),
        body: body.trim(),
        severity,
        contextType: 'inbound_webhook_event',
        contextId: event.id,
        // Snapshot the event so the ticket reproduces even after the
        // 30-day retention sweep purges the source row.
        contextSnapshot: { ...event } as unknown as Record<string, unknown>,
      }),
    onSuccess: (ticket) => {
      setSubmitted(ticket.id);
      toast.success('Issue reported — platform team has been notified');
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not report issue'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Report this event to the platform team</DialogTitle>
        </DialogHeader>
        {submitted ? (
          <div className="space-y-3 py-2 text-[13px]">
            <div className="flex items-center gap-2 text-success">
              <CheckCheck className="h-4 w-4" />
              <span className="font-medium">Ticket created</span>
            </div>
            <p className="text-muted-foreground">
              Reference&nbsp;
              <span className="font-mono text-foreground">{submitted}</span>. The
              platform team will reach out via the channel on file. Your
              snapshot of this event has been attached.
            </p>
            <div className="flex justify-end">
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-[13px]">
            <p className="text-muted-foreground">
              We&apos;ll attach a snapshot of this event automatically. Add what
              you expected to happen and anything that helps us reproduce.
            </p>
            <div className="space-y-1">
              <label className="text-[11.5px] font-medium text-foreground">
                Subject
              </label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={240}
              />
            </div>
            <div className="space-y-1">
              <label className="text-[11.5px] font-medium text-foreground">
                Severity
              </label>
              <Select
                value={severity}
                onValueChange={(v) =>
                  setSeverity(v as PlatformSupportTicketSeverity)
                }
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      <div className="flex flex-col">
                        <span>{o.label}</span>
                        <span className="text-[11px] text-muted-foreground">
                          {o.hint}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-[11.5px] font-medium text-foreground">
                Description
              </label>
              <Textarea
                rows={9}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="font-mono text-[12px]"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={() => mutation.mutate()}
                disabled={
                  mutation.isPending || !subject.trim() || !body.trim()
                }
              >
                {mutation.isPending ? 'Submitting…' : 'Submit'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
